import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Space } from "antd";
import { PictureOutlined } from "@ant-design/icons";
import { AxiosError } from "axios";
import Highlighter from "react-highlight-words";

import { IAwardType } from "../../interfaces";
import AwardTypesService from "../../services/http/services/AwardTypesService";
import { EmitSuccessAlert } from "../../utils/EmitSuccessAlert";
import { EmitError } from "../../utils/EmitError";
import { formatDateStringToDateAndTime } from "../../utils/Conversores";
import TableActionButton from "../../components/TableActionButton";
import { ThemeContext } from "../../contexts/ThemeContext";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";
import { awardImagesUrl } from "../../services/urls";

export interface ITableAwardTypeData extends IAwardType {
    key: number;
}

export default function useAwardTypes() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [tableData, setTableData] = useState<Array<ITableAwardTypeData>>([]);
    const [awardTypeBeingDeleted, setAwardTypeBeingDeleted] = useState<ITableAwardTypeData | null>(null);

    const [searchText, setSearchText] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    const navigate = useNavigate();

    const loadAwardTypes = useCallback(async (signal?: AbortSignal) => {
        try {
            const controller = signal ? undefined : new AbortController();
            const awardTypes = await AwardTypesService.listAwardTypes(signal || controller!.signal);
            const mappedData: ITableAwardTypeData[] = (awardTypes || []).map((item: IAwardType) => ({
                ...item,
                key: item.id || Math.random(),
            }));
            setTableData(mappedData);
        } catch (err) {
            if (err instanceof AxiosError && err.name === "CanceledError") {
                return;
            }
            EmitError(err, true, "awardTypes_load_error");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        setIsLoading(true);
        loadAwardTypes(controller.signal);

        return () => {
            controller.abort();
        };
    }, [loadAwardTypes]);

    const handleClickCreateNewAwardType = useCallback(() => {
        navigate("/configuracoes/tipos-de-premios/criar");
    }, [navigate]);

    const handleClickEditAwardType = useCallback(
        (data: IAwardType) => {
            navigate(`/configuracoes/tipos-de-premios/editar/${data.id}`);
        },
        [navigate]
    );

    const handleDeleteAwardType = useCallback((data: ITableAwardTypeData) => {
        setAwardTypeBeingDeleted(data);
        setIsDeleteModalVisible(true);
    }, []);

    const handleConfirmDeleteAwardType = useCallback(async () => {
        if (!awardTypeBeingDeleted?.id) return;
        try {
            setIsLoadingDelete(true);
            await AwardTypesService.deleteAwardType(awardTypeBeingDeleted.id);
            EmitSuccessAlert("Tipo de Prêmio removido com sucesso!");
            setTableData((prev) => prev.filter((item) => item.id !== awardTypeBeingDeleted.id));
            setIsDeleteModalVisible(false);
            setAwardTypeBeingDeleted(null);
        } catch (err) {
            EmitError(err, true, "awardTypes_delete_error");
        } finally {
            setIsLoadingDelete(false);
        }
    }, [awardTypeBeingDeleted]);

    const handleCancelDeleteAwardType = useCallback(() => {
        setIsDeleteModalVisible(false);
        setAwardTypeBeingDeleted(null);
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setSearchText("");
        setCurrentPage(1);
    }, []);

    const filteredData = useMemo(() => {
        let result = tableData;
        if (searchText && searchText.trim()) {
            const query = searchText.toLowerCase().trim();
            result = result.filter((item) => item.name?.toLowerCase().includes(query));
        }
        return result;
    }, [tableData, searchText]);

    const activeFilterItems = useMemo((): IActiveFilterItem[] => {
        const items: IActiveFilterItem[] = [];
        if (searchText.trim()) {
            items.push({
                key: "search",
                label: "Busca",
                value: `"${searchText}"`,
                onClear: () => {
                    setSearchText("");
                    setCurrentPage(1);
                },
            });
        }
        return items;
    }, [searchText]);

    const columns = useMemo(
        () => [
            {
                title: "Imagem",
                dataIndex: "default_image",
                key: "default_image",
                width: 90,
                align: "center" as const,
                render: (image: string) => {
                    if (image) {
                        return (
                            <Avatar
                                shape="square"
                                size={42}
                                src={`${awardImagesUrl}/${image}`}
                                style={{
                                    borderRadius: 8,
                                    border: `1px solid ${isDark ? "#303030" : "#eaedf1"}`,
                                    objectFit: "cover",
                                    backgroundColor: isDark ? "#1f1f1f" : "#f8fafc",
                                }}
                            />
                        );
                    }
                    return (
                        <Avatar
                            shape="square"
                            size={42}
                            icon={<PictureOutlined style={{ color: "#faad14" }} />}
                            style={{
                                borderRadius: 8,
                                backgroundColor: isDark ? "rgba(250, 173, 20, 0.12)" : "#fffbe6",
                                border: `1px solid ${isDark ? "#303030" : "#ffe58f"}`,
                            }}
                        />
                    );
                },
            },
            {
                title: "Nome",
                dataIndex: "name",
                key: "name",
                sorter: (a: ITableAwardTypeData, b: ITableAwardTypeData) =>
                    (a.name || "").localeCompare(b.name || ""),
                render: (text: string) => (
                    <span style={{ fontWeight: 600, fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b" }}>
                        <Highlighter
                            highlightStyle={{ backgroundColor: "#ffc069", padding: "0 2px" }}
                            searchWords={searchText ? [searchText] : []}
                            autoEscape
                            textToHighlight={text || "--"}
                        />
                    </span>
                ),
            },
            {
                title: "Data de Cadastro",
                dataIndex: "created_at",
                key: "created_at",
                width: 200,
                sorter: (a: ITableAwardTypeData, b: ITableAwardTypeData) =>
                    new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime(),
                render: (criado: string) => (
                    <span style={{ color: isDark ? "#8c8c8c" : "#64748b", fontSize: 12.5 }}>
                        {criado ? formatDateStringToDateAndTime(criado) : "--"}
                    </span>
                ),
            },
            {
                title: "Ações",
                key: "actions",
                width: 100,
                align: "center" as const,
                render: (_: any, record: ITableAwardTypeData) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Space size={6}>
                            <TableActionButton
                                action="edit"
                                tooltip="Editar tipo de prêmio"
                                onClick={() => handleClickEditAwardType(record)}
                            />
                            <TableActionButton
                                action="delete"
                                tooltip="Excluir tipo de prêmio"
                                onClick={() => handleDeleteAwardType(record)}
                            />
                        </Space>
                    </div>
                ),
            },
        ],
        [isDark, searchText, handleClickEditAwardType, handleDeleteAwardType]
    );

    return {
        isLoading,
        isLoadingDelete,
        isDeleteModalVisible,
        awardTypeBeingDeleted,
        columns,
        tableData,
        filteredData,
        searchText,
        setSearchText,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        activeFilterItems,
        handleClickCreateNewAwardType,
        handleClickEditAwardType,
        handleDeleteAwardType,
        handleConfirmDeleteAwardType,
        handleCancelDeleteAwardType,
        handleClearAllFilters,
    };
}
